import DynamicImage from '@/components/commons/dynamic-image';
import { CollectionListPageElementDataType } from '@/components/sections/builder/elements/body/collection-list-page/type';
import { cn, truncateText } from '@/lib/utils';
import { ElementType, useBuilderStore } from '@/stores/builder';
import { StoreCollections } from '@/types/http';
import Link from 'next/link';
import { shallow } from 'zustand/shallow';

type CardCollectionListType = {
  data: CollectionListPageElementDataType;
  collection: StoreCollections;
  element: ElementType;
  preview?: boolean;
};

export default function CardCollectionList({
  data,
  collection,
  element,
  preview,
}: CardCollectionListType) {
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  return (
    <>
      <div data-animation='true' data-animation-group={`collectionlist-item-${element.id}`}>
        <Link
          href={`/collections/${collection.slug}${preview ? '?isPreview=true' : ''}`}
          className='flex w-full flex-col justify-between'
        >
          <div
            className={cn(
              'card-rounded overflow-hidden duration-300 hover:scale-[1.01] hover:shadow-lg',
              data.imageRatio === 'adaptToImage' && 'img_ratio_adapt',
              data.imageRatio === 'portrait' && 'img_ratio_portrait',
              data.imageRatio === 'square' && 'img_ratio_square'
            )}
          >
            <DynamicImage
              src={collection.img_url as string}
              alt={collection.collection_name || 'Collection image'}
              width={100}
              height={100}
              className='h-full w-full object-cover'
              sizes='100vw'
            />
          </div>
          <div
            className={cn(
              'flex items-center gap-x-2 pt-2',
              data.contentAlign === 'left' && 'justify-start',
              data.contentAlign === 'center' && 'justify-center',
              data.contentAlign === 'right' && 'justify-end'
            )}
          >
            <span
              className={cn(
                'text-color-scheme  mb-2 flex  items-center gap-x-2 tracking-tight',
                data.size === 'small' && 'text-sm',
                data.size === 'medium' && 'text-base',
                data.size === 'large' && 'text-lg'
              )}
            >
              {truncateText(
                collection.collection_name,
                builder.theme.collection?.limitCharacters as number
              )}
            </span>
          </div>
        </Link>
      </div>
      <style jsx>
        {`
          .img_ratio_adapt {
            display: flex;
            align-items: center;
            justify-content: center;
            aspect-ratio: auto;
          }
          .img_ratio_portrait {
            aspect-ratio: 3 / 4;
          }
          .img_ratio_square {
            aspect-ratio: 1 / 1;
          }
          .text-color-scheme {
            color: ${data.colorSchemeCollectionList?.text};
          }
          .card-rounded {
            border-radius: ${data.borderRadius}px;
          }
        `}
      </style>
    </>
  );
}
